import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Building2, ChevronLeft, FileCheck2, MapPin, Network, Star, Store, User as UserIcon } from "lucide-react";
import { clientRoutes } from "@/lib/clientRoutes";
import { uah } from "@/lib/clientFormat";
import { useStructure } from "@/hooks/useClients";
import type { Client360Envelope } from "@/api/clients";

/**
 * Этап 4 «Структура» — read-only tree: Юрособи → Точки → Контакти, with markers
 * (primary-юрособа · per-LE debt badge · active-contract). Reached from the
 * «Відкрити структуру →» button on the Юрособи tab.
 *
 * ★ GUARDRAIL (Igor): per-LE debt badge ONLY. The tree NEVER sums LE debts into a
 * holding total — holding-AR is BI-owned (source of truth), shown elsewhere.
 * No locked visual for this view — clean DS tree per TZ §2.3, layout polish at
 * batch-приёмка.
 */
export function ClientStructureTab() {
  const { holdingId } = useParams<{ holdingId: string }>();
  const id = holdingId ?? "";
  const env = useOutletContext<Client360Envelope>();
  const navigate = useNavigate();
  const q = useStructure(id, true);

  return (
    <div className="p-6 md:p-8">
      <button
        onClick={() => navigate(clientRoutes.entities(id))}
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Юрособи
      </button>

      <div className="mb-1 flex items-center gap-2">
        <Network className="h-4 w-4 text-primary" />
        <h2 className="text-base font-semibold">Структура клієнта</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Юрособи → Точки → Контакти. Борг показано <b>по кожній юрособі</b> окремо — загальний борг холдингу (з BI) дивіться в «Огляді».
      </p>

      {q.isPending ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-muted" />)}</div>
      ) : q.isError ? (
        <div className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">Не вдалося завантажити структуру.</div>
      ) : (q.data?.legalEntities.length ?? 0) === 0 ? (
        <div className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">Юридичних осіб ще немає.</div>
      ) : (
        <div className="space-y-3">
          {q.data!.legalEntities.map((le) => (
            <div key={le.id} className="rounded-xl border bg-card">
              {/* LE node */}
              <button
                onClick={() => navigate(clientRoutes.legalEntity(id, le.id))}
                className="flex w-full items-center gap-2.5 border-b px-4 py-2.5 text-left hover:bg-muted/40"
              >
                <Building2 className="h-4 w-4 shrink-0 text-primary" />
                <span className="truncate text-sm font-semibold">{le.name}</span>
                {le.isPrimary && (
                  <span className="inline-flex shrink-0 items-center gap-0.5 rounded bg-info px-1 py-0.5 text-[10px] font-semibold text-info-foreground">
                    <Star className="h-2.5 w-2.5" />primary
                  </span>
                )}
                <span className="shrink-0 text-xs text-muted-foreground">{le.reg}</span>
                <div className="ml-auto flex shrink-0 items-center gap-1.5">
                  <span className={
                    "rounded px-1.5 py-0.5 text-[10px] font-semibold " +
                    (le.debt > 0
                      ? "bg-warning text-warning-foreground"
                      : "bg-success text-success-foreground")
                  }>
                    {le.debt > 0 ? `борг ${uah(le.debt)}` : "борг 0"}
                  </span>
                  {le.hasActiveContract && (
                    <span className="inline-flex items-center gap-0.5 rounded bg-success px-1.5 py-0.5 text-[10px] font-semibold text-success-foreground">
                      <FileCheck2 className="h-2.5 w-2.5" />договір
                    </span>
                  )}
                </div>
              </button>

              {/* children: outlets + contacts */}
              <div className="space-y-3 px-4 py-3">
                <div>
                  <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <Store className="h-3 w-3" /> Точки ({le.outlets.length})
                  </div>
                  {le.outlets.length === 0 ? (
                    <div className="pl-4 text-xs italic text-muted-foreground/70">точок немає</div>
                  ) : (
                    <ul className="space-y-1 border-l pl-3">
                      {le.outlets.map((o) => (
                        <li key={o.id} className="flex items-center gap-1.5 text-sm">
                          <span className="font-medium">{o.name || "Точка"}</span>
                          {o.location && <span className="flex items-center gap-0.5 text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{o.location}</span>}
                          {!o.active && <span className="rounded bg-muted px-1 text-[10px] text-muted-foreground">неактивна</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <UserIcon className="h-3 w-3" /> Контакти ({le.contacts.length})
                  </div>
                  {le.contacts.length === 0 ? (
                    <div className="pl-4 text-xs italic text-muted-foreground/70">контактів немає</div>
                  ) : (
                    <ul className="space-y-1 border-l pl-3">
                      {le.contacts.map((c, i) => (
                        <li key={c.id ?? i} className="flex items-center gap-1.5 text-sm">
                          <span className="font-medium">{c.name}</span>
                          {c.primary && <span className="rounded bg-primary/10 px-1 text-[10px] font-medium text-primary">основний</span>}
                          {c.role && <span className="text-xs text-muted-foreground">· {c.role}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {env?.header?.name && <div className="sr-only">{env.header.name}</div>}
    </div>
  );
}
